// Plate component — renders one plate as a clickable button with balls stacked
// inside. Supports tap selection and long-press = select the whole top run.
// The gesture is driven by pointer events: the tap action runs on pointerup,
// and the click the browser synthesizes right after it is ignored (it is the
// same gesture, already handled). This removes the old touchstart/click race
// where a long press and the (optional, arbitrarily delayed, often dropped)
// trailing click had to be reconciled by a time-guessed suppression window,
// which both swallowed genuine next taps and let trailing clicks slip through.
// A long press is a mobile gesture — only touch pointers start its timer, so
// desktop (mouse) users keep plain taps.

import { memo, useRef } from "react"

import type { Plate as PlateType } from "../sortaballs-8-colors/logic"
import type { Palette } from "./types"

import { Ball } from "./Ball"
import { DEFAULT_SIZING, sizingClass, type Sizing } from "./geometry"
import "./board-geometry.css"

interface PlateProps {
  balls: PlateType
  capacity?: number
  cssPrefix: string
  // Slot indices (0 = bottom) whose balls are hidden while a flying-ball
  // overlay animates into them. A group move hides the top `count` slots.
  hiddenSlots?: number[]
  index: number
  isSelected: boolean
  onClick: (index: number)=> void
  // Fired when the button is held down long enough (mobile long press) — the
  // caller selects the plate's full top run in one gesture.
  onLongPress: (index: number)=> void
  palette: Palette
  selectionCount?: number
  sizing?: Sizing
}

// How long to hold before a press counts as a long press.
const LONG_PRESS_MS = 500

// How far a finger may drift before the press stops being a long press — a
// scroll / drag must never select a whole run. Tracked from the pointer-down
// point; a drift past the tolerance cancels the timer.
const LONG_PRESS_MOVE_TOLERANCE_PX = 10

// Memoized: the game engine structurally shares untouched plate arrays, so
// during a move only the source + destination plates have changed props —
// default shallow compare skips the other ~10 plates' re-render (and all
// their Ball children). Callbacks are stable (the shell passes useCallback'd
// handlers) so the compare is never defeated by fresh closure identities.
export const Plate = memo(function Plate({
  balls,
  capacity = 4,
  cssPrefix,
  hiddenSlots,
  index,
  isSelected,
  onClick,
  onLongPress,
  palette,
  selectionCount = 0,
  sizing = DEFAULT_SIZING,
}: PlateProps) {
  // Long-press bookkeeping. The whole press (down → up) is owned by pointer
  // handlers: the action fires exactly once, on pointerup, and the trailing
  // click is discarded by detail — pointer-generated clicks carry detail >= 1,
  // keyboard activation carries detail 0. No lingering flags, no reset timers.
  const longPressTimer = useRef<null | ReturnType<typeof setTimeout>>(null)
  const longPressFired = useRef(false)
  const pressOrigin = useRef<null | { x: number; y: number }>(null)

  const clearLongPressTimer = ()=> {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>)=> {
    // Long press = select the whole top run is a mobile gesture; desktop
    // mice and pens keep the plain tap handled on pointerup below.
    if (event.pointerType !== "touch") return
    clearLongPressTimer()
    longPressFired.current = false
    pressOrigin.current = { x: event.clientX, y: event.clientY }
    longPressTimer.current = setTimeout(()=> {
      longPressTimer.current = null
      longPressFired.current = true
      onLongPress(index)
    }, LONG_PRESS_MS)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>)=> {
    const origin = pressOrigin.current
    if (!origin) return
    const drift = Math.hypot(event.clientX - origin.x, event.clientY - origin.y)
    if (drift > LONG_PRESS_MOVE_TOLERANCE_PX) {
      clearLongPressTimer()
      pressOrigin.current = null
    }
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLButtonElement>)=> {
    clearLongPressTimer()
    pressOrigin.current = null
    // A long press already picked the selection; the release must not apply
    // a second, tap-style action on top of it.
    if (longPressFired.current) {
      longPressFired.current = false
      return
    }
    if (
      event.pointerType === "mouse" ||
      event.pointerType === "pen" ||
      event.pointerType === "touch"
    ) {
      onClick(index)
    }
  }

  const handlePointerCancel = ()=> {
    clearLongPressTimer()
    longPressFired.current = false
    pressOrigin.current = null
  }

  const ballSize = sizing.ballSize

  // The shipped geometry (122px base / 102px compact) as a CSS size class, so
  // the plate renders from the same --sb-* numbers the board and flight overlay
  // use.
  const plateSizeClass = sizingClass(sizing)

  return (
    <button
      aria-label={`Plate ${index + 1}, ${balls.length} balls${selectionCount > 0 ? `, ${selectionCount} selected` : ""}`}
      className={`
        ${cssPrefix}-plate-button
        sb-plate-button ${plateSizeClass}
        absolute cursor-pointer transition-transform ease-(--ease-standard)
        ${
          isSelected
            ? `
              ${cssPrefix}-plate-button--selected
            `
            : ""
        }
      `}
      data-capacity={capacity}
      onClick={event=> {
        // Real pointer gestures were already handled on pointerup — their
        // synthesized click (detail >= 1) must not run the action twice.
        // Keyboard activation carries detail 0 and is the only click path.
        if (event.detail === 0) {
          onClick(index)
        }
      }}
      onPointerCancel={handlePointerCancel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      type="button"
    >
      {/* Plate body — a symmetric pill (radius = half width) so the top end
			    mirrors the bottom. */}
      <span
        className={`
          ${cssPrefix}-plate
          sb-plate-body
        `}
      />
      {/* Balls — positioned from bottom to top */}
      {balls.map((color, ballIndex)=> {
        // Position of this ball counting from the top (0 = topmost ball).
        const positionFromTop = balls.length - 1 - ballIndex
        // Picked ring only matters for a multi-ball selection — a plain
        // single-ball pick just glows the plate.
        const isPicked =
          isSelected && selectionCount >= 2 && positionFromTop < selectionCount
        return (
          <div
            className={`
              sb-ball-slot
              ${isPicked ? ` ${cssPrefix}-ball--picked` : ""}
              ${
                hiddenSlots?.includes(ballIndex)
                  ? " sb-ball-slot--hidden"
                  : ""
              }
            `}
            data-slot={ballIndex}
            key={ballIndex}
          >
            <Ball
              ballSize={ballSize}
              color={color}
              palette={palette}
            />
          </div>
        )
      })}
    </button>
  )
})
